import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, LoginCredentials } from '@/types/models';
import type { TokenResponse } from '@/types/api';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  rehydrated: boolean;
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithTokens: (tokenResponse: TokenResponse) => void;
  logout: () => void;
  refreshTokens: () => Promise<void>;
  setUser: (user: User | null) => void;
  setLoading: (isLoading: boolean) => void;
}

type AuthStore = AuthState & AuthActions;

// Lazy import to avoid circular dependency
const getAuthApi = () => import('@/services/api/authApi').then(m => m.authApi);

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      rehydrated: false,

      // Actions
      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true });
        try {
          const authApi = await getAuthApi();
          const tokenResponse = await authApi.login(credentials);
          set({
            accessToken: tokenResponse.access_token,
            refreshToken: tokenResponse.refresh_token,
            isAuthenticated: true,
          });
          const user = await authApi.getMe();
          set({ user, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      loginWithTokens: (tokenResponse: TokenResponse) => {
        set({
          accessToken: tokenResponse.access_token,
          refreshToken: tokenResponse.refresh_token,
          isAuthenticated: true,
        });
      },

      logout: () => {
        const authApi = import('@/services/api/authApi').then(m => m.authApi);
        const token = get().refreshToken;
        if (token) {
          authApi.then(api => api.logout(token)).catch(() => {/* silent */});
        }
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },

      refreshTokens: async () => {
        const currentRefreshToken = get().refreshToken;
        if (!currentRefreshToken) {
          throw new Error('No refresh token available');
        }
        const authApi = await getAuthApi();
        const tokenResponse = await authApi.refresh(currentRefreshToken);
        set({
          accessToken: tokenResponse.access_token,
          refreshToken: tokenResponse.refresh_token,
          isAuthenticated: true,
        });
      },

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: 'aflow-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => {
        return () => {
          useAuthStore.setState({ rehydrated: true });
        };
      },
    }
  )
);
